import { Component, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  debounceTime,
  distinctUntilChanged,
  first,
  forkJoin,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { CompanyListItem } from 'src/app/core/models/company/company.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { FileService } from 'src/app/core/services/file.service';

@Component({
  selector: 'app-admin-companies',
  templateUrl: './admin-companies.component.html',
  styleUrls: ['./admin-companies.component.scss'],
  standalone: false,
})
export class AdminCompaniesComponent {
  private readonly companyService = inject(CompanyService);
  private readonly fileService = inject(FileService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  public readonly notFound = Constants.NotFound;
  public readonly searchControl = new FormControl('', { nonNullable: true });
  public readonly supportModeCards = [
    {
      value: 'read',
      titleKey: 'COMPONENTS.COMPANIES.SUPPORT_MODE_READ',
      tooltipKey: 'COMPONENTS.ADMIN_PANEL.COMPANIES.READ_DESCRIPTION',
    },
    {
      value: 'write',
      titleKey: 'COMPONENTS.COMPANIES.SUPPORT_MODE_WRITE',
      tooltipKey: 'COMPONENTS.ADMIN_PANEL.COMPANIES.WRITE_DESCRIPTION',
    },
  ] as const;

  public isLoading = false;
  public selectedAccessMode: 'read' | 'write' = 'read';

  public readonly companies$ = this.searchControl.valueChanges.pipe(
    startWith(this.searchControl.value),
    debounceTime(250),
    distinctUntilChanged(),
    tap(() => (this.isLoading = true)),
    switchMap((searchTerm) =>
      this.companyService.getAdminCompanies({ searchTerm, limit: 50 }),
    ),
    switchMap((companies) =>
      this.resolveImageUrls(
        companies,
        (company) => `companies/${company.id}/title${company.titleImageExtension}`,
      ),
    ),
    tap(() => (this.isLoading = false)),
    shareReplay(1),
  );

  public openCompany(company: CompanyListItem): void {
    this.authService
      .createSupportSession(company.id, this.selectedAccessMode)
      .pipe(
        first(),
        tap(() => {
          void this.router.navigateByUrl(`companies/${company.id}/projects`);
        }),
      )
      .subscribe();
  }

  public setAccessMode(accessMode: 'read' | 'write'): void {
    this.selectedAccessMode = accessMode;
  }

  private resolveImageUrls<T extends { titleImageExtension?: string; imageUrl?: string }>(
    items: T[],
    getPath: (item: T) => string,
  ): Observable<T[]> {
    if (!items.length) {
      return of([]);
    }

    return forkJoin(
      items.map((item) => {
        if (!item.titleImageExtension) {
          item.imageUrl = Constants.NotFound;
          return of(item);
        }

        return this.fileService.getEntityWithImageUrl(item, getPath(item));
      }),
    );
  }
}
